import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Grid, Box } from "@mui/material";
import TurmaComboBox from "../Turma/UI/TurmaComboBox";
import DisciplinaComboBox from "../Disciplina/UI/DisciplinaComboBox";
import UsuarioPerfilComboBox from "../Usuario/UI/UsuarioPerfilComboBox";
import Loading from "../UI/Loading";

// Evita repetir o mesmo objeto de breakpoints em cada <Grid>
const filterColSize = { xs: 12, sm: 6, md: 3, lg: 2, xl: 2 };

const TDPTableSelectView = ({
  tdpsAll,
  tdps,
  setTdps,
  setTdp,
  isLoading,
  usuariosPerfil,
  usuarioPerfil,
  setUsuarioPerfil,
  turmas,
  disciplinas,
  disciplina,
  setDisciplina,
  turma,
  setTurma,
}) => {
  const hasActiveFilter = Boolean(turma || disciplina || usuarioPerfil);

  const handleLimparFiltro = () => {
    setTdps(tdpsAll);
    setTurma(null);
    setDisciplina(null);
    setUsuarioPerfil(null);
  };

  const handleUsuarioPerfilChange = (event) => {
    const usuarioPerfilSelect = usuariosPerfil.find(
      (up) => up.codigo === event.target.value,
    );
    setUsuarioPerfil(usuarioPerfilSelect);
    setTdps(
      tdpsAll.filter(
        (prevState) =>
          prevState.usuario &&
          prevState.usuario.id === usuarioPerfilSelect.usuario.id,
      ),
    );
    setTurma(null);
    setDisciplina(null);
  };

  const handleTurmaChange = (event) => {
    const turmaSelect = turmas.find((t) => t.codigo === event.target.value);
    setTurma(turmaSelect);
    setTdps(
      tdpsAll.filter(
        (prevState) => prevState.turma.codigo === turmaSelect.codigo,
      ),
    );
    setDisciplina(null);
    setUsuarioPerfil(null);
  };

  const handleDisciplinaChange = (event) => {
    const disciplinaSelect = disciplinas.find(
      (d) => d.codDisciplina === event.target.value,
    );
    setDisciplina(disciplinaSelect);
    setTdps(
      tdpsAll.filter(
        (prevState) =>
          prevState.disciplina.codDisciplina === disciplinaSelect.codDisciplina,
      ),
    );
    setTurma(null);
    setUsuarioPerfil(null);
  };

  const linhasVazias = useMemo(
    () => !isLoading && tdps.length === 0,
    [isLoading, tdps],
  );

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Selecione uma turma - disciplina - professor
      </Typography>

      <Box sx={{ mt: 2.5, mb: 3.5 }}>
        <Grid container direction="row" alignItems="center" spacing={3}>
          <Grid size={filterColSize}>
            <TurmaComboBox
              turmas={turmas}
              turma={turma}
              handleTurmaChange={handleTurmaChange}
            />
          </Grid>
          <Grid size={filterColSize}>
            <DisciplinaComboBox
              disciplinas={disciplinas}
              disciplina={disciplina}
              handleDisciplinaChange={handleDisciplinaChange}
            />
          </Grid>
          <Grid size={filterColSize}>
            <UsuarioPerfilComboBox
              usuariosPerfil={usuariosPerfil}
              usuarioPerfil={usuarioPerfil}
              handleUsuarioPerfilChange={handleUsuarioPerfilChange}
            />
          </Grid>
          <Grid size={filterColSize}>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleLimparFiltro}
              disabled={!hasActiveFilter}
              fullWidth
            >
              Limpar filtro
            </Button>
          </Grid>
        </Grid>

        {hasActiveFilter && (
          <Box sx={{ mt: 1.5 }}>
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`${tdps.length} resultado${tdps.length === 1 ? "" : "s"} filtrado${
                tdps.length === 1 ? "" : "s"
              }`}
            />
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Grid container justifyContent="center">
          <Grid>
            <Loading description="Carregando Turma-Disciplina-Professor" />
          </Grid>
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
          <Table
            size="small"
            aria-label="Tabela de turma, disciplina e professor"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Turma (Curso)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Disciplina</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Professor</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhasVazias ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum registro encontrado para o filtro selecionado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tdps.map((tdp) => (
                  <TableRow key={tdp.codigo} hover>
                    <TableCell>
                      {tdp.turma.descricao} ({tdp.turma.curso.abreviacao})
                    </TableCell>
                    <TableCell>{tdp.disciplina.descricao}</TableCell>
                    <TableCell>
                      {tdp.usuario ? tdp.usuario.nome : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        type="button"
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => setTdp(tdp)}
                      >
                        Selecionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TDPTableSelectView;
